import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTestManagedAccountPaymentsManagerConnection } from '@/api/hooks/managed-accounts/useTestManagedAccountPaymentsManagerConnection';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import CpdActionButton from '@/components/ui/CpdActionButton';
import ConnectionTestResult from './components/ConnectionTestResult';
import FilePrefixesSection from './components/FilePrefixesSection';
import FolderConfigurationSection from './components/FolderConfigurationSection';
import ReconFoldersSection from './components/ReconFoldersSection';
import ServerDetailsSection from './components/ServerDetailsSection';
import SftpAuthenticationSection from './components/SftpAuthenticationSection';
import { usePaymentsManagerConfigurationForm } from './hooks/usePaymentsManagerConfigurationForm';

const PaymentsManagerConfigurationForm = ({
  managedAccountId,
  configuration,
}) => {
  const { t } = useTranslation();
  const {
    testing,
    testResult,
    testConnection,
    clearTestResult,
  } = useTestManagedAccountPaymentsManagerConnection(managedAccountId);
  const formState = usePaymentsManagerConfigurationForm({
    managedAccountId,
    configuration,
    onSaved: clearTestResult,
  });
  const disabled = formState.saving || testing;

  const handleTestConnection = async () => {
    try {
      await testConnection();
    } catch {
      toast.error(t('managedAccounts.paymentsManager.test.failed'));
    }
  };

  return (
    <Box component='form' onSubmit={formState.save} noValidate>
      <FormErrorSummary errors={formState.generalErrors} />
      <ServerDetailsSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        onChange={formState.updateField}
        disabled={disabled}
      />
      <SftpAuthenticationSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        passwordConfigured={formState.passwordConfigured}
        privateKeyConfigured={formState.privateKeyConfigured}
        onFieldChange={formState.updateField}
        onModeChange={formState.updateAuthenticationMode}
        disabled={disabled}
      />
      <FolderConfigurationSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        onChange={formState.updateField}
        disabled={disabled}
      />
      <FilePrefixesSection
        form={formState.form}
        fieldErrors={formState.fieldErrors}
        onChange={formState.updateField}
        disabled={disabled}
      />
      <ReconFoldersSection
        reconPaths={formState.reconPaths}
        fieldErrors={formState.fieldErrors}
        onAdd={formState.addReconPath}
        onUpdate={formState.updateReconPath}
        onRemove={formState.removeReconPath}
        disabled={disabled}
      />
      <ConnectionTestResult result={testResult} />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <CpdActionButton
          type='button'
          variant='outlined'
          loading={testing}
          disabled={formState.saving || !formState.hasSavedConfiguration}
          sx={{
            '&&': { color: 'primary.main', borderColor: 'primary.main' },
            '&&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.dark',
            },
            '&&.Mui-disabled': {
              color: 'text.disabled',
              borderColor: 'action.disabledBackground',
            },
          }}
          onClick={handleTestConnection}>
          {t('managedAccounts.paymentsManager.test.action')}
        </CpdActionButton>
        <CpdActionButton
          type='submit'
          loading={formState.saving}
          disabled={testing}>
          Save configuration
        </CpdActionButton>
      </Box>
    </Box>
  );
};

export default PaymentsManagerConfigurationForm;
